import type { LngLat } from "../lib/geo";

export type VehicleKind = "bus" | "collector" | "machinery";
export type BusTripDirection = "to_asuncion" | "to_piribebuy";
export type VehicleStatus =
  | "EN RUTA"
  | "DETENIDO"
  | "RECOLECTANDO"
  | "EN TRÁNSITO"
  | "PAUSADO"
  | "COMPLETADO"
  | "FUERA DE SERVICIO"
  | "SIN SEÑAL"
  | "SIMULACIÓN";

export interface MobilityStop {
  id: string;
  name: string;
  progress: number;
  estimatedStopSeconds: number;
  zone?: string;
}

export interface MobilityRoute {
  id: string;
  name: string;
  kind: VehicleKind;
  coordinates: readonly LngLat[];
  stops: readonly MobilityStop[];
  disclaimer?: string;
}

export interface MobilityVehicle {
  id: string;
  trackingCode: string;
  internalNumber: string;
  name: string;
  company: string;
  kind: VehicleKind;
  routeId: string;
  direction: string;
  tripDirection?: BusTripDirection;
  initialProgress?: number;
  scheduleLabel?: string;
  occupancy?: "Baja" | "Media" | "Alta";
}

// Geometrías obtenidas sobre la red vial de OpenStreetMap mediante OSRM.
// El tramo de la Línea 197 sigue el itinerario publicado de La Caacupeña S.A.
// Coordenadas GeoJSON: [longitud, latitud]. No son líneas entre ciudades.
export const busPiribebuyRoute: readonly LngLat[] = [
  [-57.045511, -25.382219],
  [-57.045023, -25.384422],
  [-57.045027, -25.389951],
  [-57.045059, -25.395731],
  [-57.045143, -25.398937],
  [-57.045479, -25.399826],
  [-57.049099, -25.404852],
  [-57.050976, -25.407497],
  [-57.051405, -25.408679],
  [-57.051683, -25.413609],
  [-57.05148, -25.414369],
  [-57.050838, -25.414988],
  [-57.047836, -25.416501],
  [-57.046423, -25.41745],
  [-57.04578, -25.418379],
  [-57.045498, -25.419829],
  [-57.045756, -25.425521],
  [-57.046226, -25.427116],
  [-57.047311, -25.428784],
  [-57.047506, -25.429448],
  [-57.047332, -25.432536],
  [-57.046995, -25.436244],
  [-57.046592, -25.440681],
  [-57.046418, -25.443058],
  [-57.046213, -25.445533],
  [-57.045745, -25.448155],
  [-57.044607, -25.452432],
  [-57.044593, -25.453101],
  [-57.045142, -25.456226],
  [-57.045344, -25.457471],
  [-57.045356, -25.458172],
  [-57.044773, -25.459239],
  [-57.043597, -25.460908],
  [-57.0432, -25.463044],
  [-57.042977, -25.464349],
  [-57.045883, -25.464721],
  [-57.048781, -25.465018],
  [-57.048634, -25.466234],
  [-57.047933, -25.466132],
];

// Estación de Buses de Asunción → Cuatro Mojones → Abasto → Calle Última.
// El último punto enlaza con el tramo metropolitano que continúa a San Lorenzo.
const busAsuncionTerminalRoute: readonly LngLat[] = [
  [-57.595746, -25.325932],
  [-57.595902, -25.325159],
  [-57.595838, -25.325033],
  [-57.595594, -25.325002],
  [-57.594923, -25.325945],
  [-57.594681, -25.326782],
  [-57.594771, -25.327024],
  [-57.595459, -25.327575],
  [-57.594665, -25.328213],
  [-57.59362, -25.329333],
  [-57.589992, -25.334204],
  [-57.588736, -25.335234],
  [-57.586749, -25.337042],
  [-57.584918, -25.339355],
  [-57.584156, -25.340135],
  [-57.583101, -25.340992],
  [-57.581807, -25.339935],
  [-57.582524, -25.339312],
  [-57.583526, -25.338218],
  [-57.584387, -25.33884],
  [-57.58486, -25.339328],
  [-57.587078, -25.343091],
  [-57.587125, -25.343058],
  [-57.58621, -25.341417],
  [-57.585049, -25.339483],
  [-57.584435, -25.338785],
  [-57.569754, -25.327436],
  [-57.569925, -25.327219],
  [-57.569702, -25.327457],
  [-57.574069, -25.330851],
  [-57.574637, -25.330264],
  [-57.574838, -25.329935],
  [-57.575123, -25.328951],
  [-57.574937, -25.326095],
  [-57.574512, -25.324478],
  [-57.572662, -25.318537],
  [-57.570757, -25.319032],
  [-57.570288, -25.31754],
  [-57.564123, -25.319131],
  [-57.563826, -25.31834],
  [-57.563506, -25.317945],
  [-57.563296, -25.316866],
  [-57.563102, -25.314789],
  [-57.564081, -25.314681],
  [-57.564405, -25.31665],
  [-57.561052, -25.317307],
  [-57.561275, -25.318732],
  [-57.561283, -25.319905],
  [-57.560034, -25.320312],
  [-57.553934, -25.323514],
  [-57.555237, -25.324913],
  [-57.554087, -25.326243],
  [-57.558925, -25.332801],
  [-57.563067, -25.338146],
];

export const busFullRoute: readonly LngLat[] = [
  // Geometría vial completa calculada con OSRM sobre datos de OpenStreetMap.
  // Se conserva una tolerancia máxima aproximada de 5 m al simplificarla.
  ...busAsuncionTerminalRoute,
  [-57.562702, -25.33837],
  [-57.563067, -25.338146],
  [-57.562121, -25.336894],
  [-57.561875, -25.336963],
  [-57.556966, -25.339114],
  [-57.547582, -25.34388],
  [-57.544864, -25.339435],
  [-57.540155, -25.332468],
  [-57.538702, -25.333256],
  [-57.537687, -25.3337],
  [-57.521578, -25.339691],
  [-57.519876, -25.340757],
  [-57.518893, -25.341585],
  [-57.517847, -25.342689],
  [-57.517122, -25.343773],
  [-57.508944, -25.33994],
  [-57.506374, -25.344458],
  [-57.500455, -25.341706],
  [-57.499603, -25.341375],
  [-57.49907, -25.341317],
  [-57.488897, -25.34361],
  [-57.479253, -25.345545],
  [-57.474624, -25.346723],
  [-57.449232, -25.350111],
  [-57.446482, -25.351361],
  [-57.444388, -25.352427],
  [-57.443554, -25.353144],
  [-57.439467, -25.357175],
  [-57.438812, -25.357676],
  [-57.433009, -25.359856],
  [-57.431864, -25.360447],
  [-57.420162, -25.367755],
  [-57.415725, -25.370871],
  [-57.403504, -25.377344],
  [-57.402418, -25.377553],
  [-57.382058, -25.379104],
  [-57.380779, -25.379375],
  [-57.379525, -25.379963],
  [-57.371874, -25.383987],
  [-57.362536, -25.388387],
  [-57.348379, -25.395709],
  [-57.347658, -25.395978],
  [-57.324493, -25.399669],
  [-57.316019, -25.401464],
  [-57.308154, -25.401682],
  [-57.297562, -25.403264],
  [-57.29663, -25.40359],
  [-57.294939, -25.404641],
  [-57.294428, -25.40479],
  [-57.293919, -25.404781],
  [-57.2932, -25.404529],
  [-57.280259, -25.398233],
  [-57.279716, -25.397789],
  [-57.279098, -25.396984],
  [-57.277774, -25.393974],
  [-57.277499, -25.393562],
  [-57.276836, -25.392809],
  [-57.274236, -25.390533],
  [-57.273248, -25.389979],
  [-57.271353, -25.38943],
  [-57.266665, -25.38788],
  [-57.266002, -25.387543],
  [-57.265322, -25.386896],
  [-57.262889, -25.383838],
  [-57.262446, -25.383448],
  [-57.2619, -25.38314],
  [-57.251765, -25.381323],
  [-57.246767, -25.380038],
  [-57.238938, -25.3783],
  [-57.222899, -25.37324],
  [-57.221815, -25.373017],
  [-57.211843, -25.372292],
  [-57.210823, -25.372344],
  [-57.209841, -25.372593],
  [-57.208887, -25.373052],
  [-57.208212, -25.373573],
  [-57.207083, -25.375064],
  [-57.204668, -25.377034],
  [-57.203931, -25.377484],
  [-57.201716, -25.378344],
  [-57.201375, -25.378669],
  [-57.200592, -25.380013],
  [-57.200377, -25.38019],
  [-57.199551, -25.380592],
  [-57.199091, -25.380934],
  [-57.198762, -25.38144],
  [-57.198654, -25.381961],
  [-57.198924, -25.383477],
  [-57.198819, -25.38605],
  [-57.19852, -25.386698],
  [-57.19793, -25.387233],
  [-57.197064, -25.387532],
  [-57.181078, -25.389611],
  [-57.17992, -25.389608],
  [-57.177899, -25.389174],
  [-57.177331, -25.389301],
  [-57.176897, -25.388914],
  [-57.17322, -25.387955],
  [-57.171597, -25.387261],
  [-57.170578, -25.386597],
  [-57.169394, -25.385625],
  [-57.154649, -25.372025],
  [-57.153262, -25.370862],
  [-57.152113, -25.370124],
  [-57.150489, -25.369407],
  [-57.14879, -25.368973],
  [-57.147467, -25.368805],
  [-57.146439, -25.368814],
  [-57.144914, -25.368992],
  [-57.114619, -25.374393],
  [-57.112994, -25.375023],
  [-57.112015, -25.37556],
  [-57.111013, -25.376285],
  [-57.101232, -25.385635],
  [-57.099609, -25.386653],
  [-57.098659, -25.387082],
  [-57.097368, -25.387536],
  [-57.097155, -25.3878],
  [-57.096436, -25.387675],
  [-57.095228, -25.387801],
  [-57.085086, -25.388255],
  [-57.084232, -25.388429],
  [-57.076161, -25.390896],
  [-57.075326, -25.391089],
  [-57.072195, -25.391078],
  [-57.07022, -25.390881],
  [-57.063011, -25.388863],
  [-57.061503, -25.388405],
  [-57.060632, -25.388019],
  [-57.058012, -25.386255],
  [-57.056767, -25.385696],
  [-57.045516, -25.3823],
  [-57.04528, -25.382315],
  [-57.045087, -25.382497],
  [-57.045023, -25.38296],
  [-57.045028, -25.391768],
  [-57.045143, -25.398937],
  [-57.045479, -25.399826],
  [-57.05076, -25.407149],
  [-57.051267, -25.408043],
  [-57.051405, -25.408679],
  [-57.051683, -25.413609],
  [-57.05148, -25.414369],
  [-57.050838, -25.414988],
  [-57.047288, -25.416794],
  [-57.046582, -25.417282],
  [-57.045998, -25.417973],
  [-57.04578, -25.418379],
  [-57.045533, -25.41931],
  [-57.045498, -25.419829],
  [-57.045801, -25.425977],
  [-57.046053, -25.42682],
  [-57.04722, -25.428583],
  [-57.047444, -25.42911],
  [-57.047506, -25.429448],
  [-57.047332, -25.432536],
  [-57.046075, -25.446778],
  [-57.044575, -25.452659],
  [-57.044675, -25.453861],
  [-57.045142, -25.456226],
  [-57.045387, -25.458005],
  [-57.045193, -25.458623],
  [-57.043597, -25.460908],
  [-57.042977, -25.464349],
  [-57.048781, -25.465018],
  [-57.048815, -25.465141],
  [-57.048634, -25.466234],
  [-57.039521, -25.464954],
  [-57.039023, -25.46838],
  [-57.0391, -25.469707],
  [-57.038898, -25.472086],
  [-57.038035, -25.479],
  [-57.037847, -25.479779],
  [-57.03709, -25.481082],
  [-57.032136, -25.48665],
  [-57.032894, -25.486902],
  [-57.033428, -25.487179],
  [-57.037164, -25.490631],
  [-57.03961, -25.492549],
  [-57.040238, -25.491768],
];

export const busPiribebuyToAsuncionRoute: readonly LngLat[] = [
  ...busFullRoute,
].reverse();

export const collectorRoute: readonly LngLat[] = [
  [-57.044899, -25.459055],
  [-57.043963, -25.460389],
  [-57.04273, -25.461672],
  [-57.041173, -25.462787],
  [-57.039922, -25.463947],
  [-57.039397, -25.465753],
  [-57.039023, -25.46838],
  [-57.039771, -25.468464],
  [-57.03912, -25.467429],
  [-57.039625, -25.46459],
  [-57.040316, -25.463999],
  [-57.042977, -25.464349],
  [-57.045883, -25.464721],
  [-57.048781, -25.465018],
  [-57.048634, -25.466234],
  [-57.047112, -25.46601],
  [-57.046714, -25.468257],
  [-57.046517, -25.469403],
  [-57.049493, -25.469831],
  [-57.050723, -25.471915],
  [-57.052574, -25.471586],
  [-57.055146, -25.471067],
  [-57.058188, -25.469919],
  [-57.059813, -25.468577],
  [-57.06161, -25.468379],
  [-57.063588, -25.467627],
  [-57.06604, -25.466474],
  [-57.0665, -25.466183],
  [-57.064924, -25.465371],
  [-57.06282, -25.464706],
  [-57.061083, -25.464304],
  [-57.058703, -25.464526],
  [-57.056723, -25.465014],
  [-57.053644, -25.465481],
  [-57.051896, -25.46547],
  [-57.052263, -25.46282],
  [-57.052622, -25.460184],
  [-57.051166, -25.460009],
  [-57.051532, -25.457107],
  [-57.051893, -25.455664],
  [-57.053573, -25.4559],
  [-57.051893, -25.455664],
  [-57.05044, -25.456144],
  [-57.050127, -25.456983],
  [-57.048285, -25.456722],
  [-57.046664, -25.456522],
  [-57.047209, -25.454568],
  [-57.047635, -25.453049],
  [-57.044675, -25.453861],
  [-57.045162, -25.456347],
  [-57.0454, -25.457814],
  [-57.044899, -25.459055],
];

export const mobilityRoutes: readonly MobilityRoute[] = [
  {
    id: "bus-piriy-local",
    name: "Acceso PY02 → Terminal Piribebuy",
    kind: "bus",
    coordinates: busPiribebuyRoute,
    stops: [
      {
        id: "stop-cruce",
        name: "Cruce PY02 / Ramal Piribebuy",
        progress: 0,
        estimatedStopSeconds: 12,
      },
      {
        id: "stop-guazu",
        name: "Guazú Rocai",
        progress: 0.28,
        estimatedStopSeconds: 10,
      },
      {
        id: "stop-san-blas",
        name: "San Blas",
        progress: 0.67,
        estimatedStopSeconds: 14,
      },
      {
        id: "stop-centro",
        name: "Centro de Piribebuy",
        progress: 0.9,
        estimatedStopSeconds: 16,
      },
      {
        id: "stop-terminal",
        name: "Terminal de Ómnibus",
        progress: 1,
        estimatedStopSeconds: 18,
      },
    ],
  },
  {
    id: "bus-asuncion-full",
    name: "LÍNEA 197 · ASUNCIÓN → PIRIBEBUY",
    kind: "bus",
    coordinates: busFullRoute,
    stops: [
      {
        id: "full-asu",
        name: "Estación de Buses de Asunción",
        progress: 0,
        estimatedStopSeconds: 24,
      },
      {
        id: "full-4m",
        name: "Cuatro Mojones",
        progress: 0.027,
        estimatedStopSeconds: 14,
      },
      {
        id: "full-abasto",
        name: "Mercado de Abasto",
        progress: 0.066,
        estimatedStopSeconds: 16,
      },
      {
        id: "full-calle-ultima",
        name: "Calle Última",
        progress: 0.151,
        estimatedStopSeconds: 14,
      },
      { id: "full-sl", name: "San Lorenzo", progress: 0.245, estimatedStopSeconds: 16 },
      { id: "full-cap", name: "Capiatá", progress: 0.334, estimatedStopSeconds: 14 },
      { id: "full-ita", name: "Itauguá", progress: 0.451, estimatedStopSeconds: 14 },
      { id: "full-ypa", name: "Ypacaraí", progress: 0.518, estimatedStopSeconds: 14 },
      { id: "full-caa", name: "Caacupé", progress: 0.702, estimatedStopSeconds: 18 },
      {
        id: "full-access",
        name: "Acceso a Piribebuy",
        progress: 0.828,
        estimatedStopSeconds: 12,
      },
      {
        id: "full-piri-centro",
        name: "Centro de Piribebuy",
        progress: 0.941,
        estimatedStopSeconds: 20,
      },
      {
        id: "full-base",
        name: "Base La Caacupeña",
        progress: 1,
        estimatedStopSeconds: 18,
      },
    ],
    disclaimer:
      "Recorrido vial de referencia de la Línea 197; la posición solo se publica cuando existe telemetría GPS.",
  },
  {
    id: "bus-piribebuy-asuncion",
    name: "LÍNEA 197 · PIRIBEBUY → ASUNCIÓN",
    kind: "bus",
    coordinates: busPiribebuyToAsuncionRoute,
    stops: [
      {
        id: "return-base",
        name: "Base La Caacupeña",
        progress: 0,
        estimatedStopSeconds: 18,
      },
      {
        id: "return-centro",
        name: "Centro de Piribebuy",
        progress: 0.059,
        estimatedStopSeconds: 20,
      },
      {
        id: "return-access",
        name: "Acceso a Piribebuy",
        progress: 0.172,
        estimatedStopSeconds: 12,
      },
      { id: "return-caa", name: "Caacupé", progress: 0.298, estimatedStopSeconds: 18 },
      { id: "return-ypa", name: "Ypacaraí", progress: 0.482, estimatedStopSeconds: 14 },
      { id: "return-ita", name: "Itauguá", progress: 0.549, estimatedStopSeconds: 14 },
      { id: "return-cap", name: "Capiatá", progress: 0.666, estimatedStopSeconds: 14 },
      {
        id: "return-sl",
        name: "San Lorenzo",
        progress: 0.755,
        estimatedStopSeconds: 16,
      },
      {
        id: "return-calle-ultima",
        name: "Calle Última",
        progress: 0.849,
        estimatedStopSeconds: 14,
      },
      {
        id: "return-abasto",
        name: "Mercado de Abasto",
        progress: 0.934,
        estimatedStopSeconds: 16,
      },
      {
        id: "return-4m",
        name: "Cuatro Mojones",
        progress: 0.973,
        estimatedStopSeconds: 14,
      },
      {
        id: "return-asu",
        name: "Estación de Buses de Asunción",
        progress: 1,
        estimatedStopSeconds: 24,
      },
    ],
    disclaimer:
      "Recorrido vial de referencia de la Línea 197; la posición solo se publica cuando existe telemetría GPS.",
  },
  {
    id: "collector-reference",
    name: "RECORRIDO DE REFERENCIA — RECOLECTOR 01",
    kind: "collector",
    coordinates: collectorRoute,
    disclaimer: "Recorrido de referencia sujeto a validación municipal.",
    stops: [
      {
        id: "zone-centro",
        name: "Centro",
        zone: "Centro",
        progress: 0.04,
        estimatedStopSeconds: 18,
      },
      {
        id: "zone-santa-ana",
        name: "Santa Ana",
        zone: "Santa Ana",
        progress: 0.22,
        estimatedStopSeconds: 22,
      },
      {
        id: "zone-rosario",
        name: "Virgen del Rosario",
        zone: "Virgen del Rosario",
        progress: 0.43,
        estimatedStopSeconds: 20,
      },
      {
        id: "zone-aux",
        name: "María Auxiliadora",
        zone: "María Auxiliadora",
        progress: 0.66,
        estimatedStopSeconds: 24,
      },
      {
        id: "zone-san-blas",
        name: "San Blas",
        zone: "San Blas",
        progress: 0.85,
        estimatedStopSeconds: 18,
      },
      {
        id: "zone-exit",
        name: "Salida hacia disposición final",
        progress: 1,
        estimatedStopSeconds: 8,
      },
    ],
  },
];

export const mobilityVehicles: readonly MobilityVehicle[] = [
  {
    id: "bus-001",
    trackingCode: "BUS-001",
    internalNumber: "BUS 01",
    name: "Unidad 01",
    company: "La Caacupeña S.A.",
    kind: "bus",
    routeId: "bus-asuncion-full",
    direction: "A Piribebuy",
    tripDirection: "to_piribebuy",
    initialProgress: 0.01,
    scheduleLabel: "Vuelta actual · Terminal Asunción",
    occupancy: "Media",
  },
  {
    id: "bus-002",
    trackingCode: "BUS-002",
    internalNumber: "BUS 02",
    name: "Unidad 02",
    company: "La Caacupeña S.A.",
    kind: "bus",
    routeId: "bus-asuncion-full",
    direction: "A Piribebuy",
    tripDirection: "to_piribebuy",
    initialProgress: 0.242,
    scheduleLabel: "Vuelta −1 h · San Lorenzo",
    occupancy: "Baja",
  },
  {
    id: "bus-003",
    trackingCode: "BUS-003",
    internalNumber: "BUS 03",
    name: "Unidad 03",
    company: "La Caacupeña S.A.",
    kind: "bus",
    routeId: "bus-asuncion-full",
    direction: "A Piribebuy",
    tripDirection: "to_piribebuy",
    initialProgress: 0.448,
    scheduleLabel: "Vuelta −2 h · Itauguá",
    occupancy: "Media",
  },
  {
    id: "bus-004",
    trackingCode: "BUS-004",
    internalNumber: "BUS 04",
    name: "Unidad 04",
    company: "La Caacupeña S.A.",
    kind: "bus",
    routeId: "bus-piribebuy-asuncion",
    direction: "A Asunción",
    tripDirection: "to_asuncion",
    initialProgress: 0.04,
    scheduleLabel: "Ida actual · Piribebuy",
    occupancy: "Alta",
  },
  {
    id: "bus-005",
    trackingCode: "BUS-005",
    internalNumber: "BUS 05",
    name: "Unidad 05",
    company: "La Caacupeña S.A.",
    kind: "bus",
    routeId: "bus-piribebuy-asuncion",
    direction: "A Asunción",
    tripDirection: "to_asuncion",
    initialProgress: 0.295,
    scheduleLabel: "Ida −1 h · Caacupé",
    occupancy: "Media",
  },
  {
    id: "bus-006",
    trackingCode: "BUS-006",
    internalNumber: "BUS 06",
    name: "Unidad 06",
    company: "La Caacupeña S.A.",
    kind: "bus",
    routeId: "bus-piribebuy-asuncion",
    direction: "A Asunción",
    tripDirection: "to_asuncion",
    initialProgress: 0.479,
    scheduleLabel: "Ida −2 h · Ypacaraí",
    occupancy: "Baja",
  },
  {
    id: "collector-001",
    trackingCode: "COLLECTOR-001",
    internalNumber: "RECOLECTOR 01",
    name: "Recolector 01",
    company: "Servicio municipal",
    kind: "collector",
    routeId: "collector-reference",
    direction: "Circuito urbano de referencia",
  },
];

export function routeById(id: string) {
  const route = mobilityRoutes.find((item) => item.id === id);
  if (!route) throw new Error(`Ruta no encontrada: ${id}`);
  return route;
}
